/**
 * LawBillityImportPanel — bring a matter's unbilled LawBillity time into
 * CaseMinder (BM-H4392).
 *
 * Vanessa, 16 Oct 2026: "can we pull time from LB to CM per matter during
 * transitional phase rather than handbuild the WIP?" Directors, accounts and
 * fee earners (27 Oct 2026: the server lets each fee earner do their own
 * matters only). Preview first; Import writes each LawBillity entry once, priced at
 * the CaseMinder rate for the fee earner's grade, so pressing it twice is
 * harmless. LawBillity minutes round up to the 6 minute unit. Entries that
 * look like time already typed into CaseMinder (same person, day and length)
 * are flagged and left out unless ticked.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Download } from "lucide-react";
import { API } from "../config/api";
import { getUserRole, ROLES } from "../config/roleConfig";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "./ui/button";

const ALREADY_IMPORTED = "already imported";

const RATE_LABEL = { standard: "Standard", complex_expedite: "Complex / expedite" };

// BM-H4833: the signed-in email, as CreditControlWidget reads it. bonsai_user_id
// is the session's user id, not an email, so it gave no role and hid the button.
function currentEmail() {
  try {
    return sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "";
  } catch {
    return "";
  }
}

export function canImportLawBillityTime(email) {
  return [ROLES.DIRECTOR, ROLES.ACCOUNTS, ROLES.RESI_FE, ROLES.COMM_PROP, ROLES.COCO_LIT]
    .includes(getUserRole(email));
}

// inline: the button sits in its parent's flex row of actions (matter page Time
// heading, 4 Nov 2026); the preview and any error drop onto their own full-width line.
export default function LawBillityImportPanel({ matterRef, onImported, inline = false }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rateType, setRateType] = useState("standard");
  const [ticked, setTicked] = useState(() => new Set());

  if (!canImportLawBillityTime(currentEmail())) return null;

  const loadPreview = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await axios.get(`${API}/lawbillity-time-import/preview`, {
        params: { matter_ref: matterRef }, withCredentials: true,
      });
      setPreview(resp.data);
      setTicked(new Set());
      if (resp.data.rate_type) setRateType(resp.data.rate_type);
    } catch (err) {
      setPreview(null);
      setError(errorMessage(err, "LawBillity could not be read"));
    } finally {
      setLoading(false);
    }
  };

  const runImport = async () => {
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/lawbillity-time-import`,
        { matter_ref: matterRef, rate_type: rateType, include_duplicates: [...ticked] },
        { withCredentials: true });
      const r = resp.data;
      if (r.failed.length) {
        toast.warning(`Imported ${r.imported.length}; ${r.failed.length} could not be imported: ${r.failed[0].reason}`);
      } else {
        toast.success(`Imported ${r.imported.length} entries, ${r.imported_hours} hours`);
      }
      onImported?.();
      await loadPreview();
    } catch (err) {
      toast.error(errorMessage(err, "Import failed"));
      setLoading(false);
    }
  };

  const rows = preview?.import || [];
  // 3 Nov 2026 (Maugey): say why each entry was left out. "Already imported"
  // is counted on its own, not as a skip.
  const notIn = (preview?.skipped || []).filter((s) => s.reason !== ALREADY_IMPORTED);
  const byReason = notIn.reduce((acc, s) => {
    (acc[s.reason] = acc[s.reason] || []).push(s);
    return acc;
  }, {});
  const isDup = (c) => !!c.possible_duplicate_of;
  const n = rows.filter((c) => !isDup(c) || ticked.has(String(c.lawbillity_id))).length;
  const dups = rows.filter(isDup).length;
  const toggle = (id) => setTicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  return (
    <div className={inline ? "contents" : "mb-4"} data-testid="lb-import-panel">
      {!preview && (
        <Button variant="outline" size="sm" onClick={loadPreview} disabled={loading} data-testid="lb-import-preview-btn">
          <Download className="w-3.5 h-3.5 mr-1" />
          {loading ? "Reading LawBillity…" : "From LawBillity"}
        </Button>
      )}
      {error && <p className={`text-xs text-red-400 mt-2 ${inline ? "order-last basis-full" : ""}`} data-testid="lb-import-error">{error}</p>}
      {preview && (
        <div className={`border border-white/10 rounded-md p-3 text-xs text-slate-300 ${inline ? "order-last basis-full mt-1" : ""}`} data-testid="lb-import-preview">
          <p className="mb-2">
            <span className="text-white font-semibold">{n} unbilled {n === 1 ? "entry" : "entries"}, {preview.import_hours} h</span>
            {" "}to bring in. {preview.already_imported} already imported, {notIn.length} not brought in.
          </p>
          {notIn.length > 0 && (
            <div className="mb-2" data-testid="lb-import-skips">
              {Object.entries(byReason).map(([reason, list]) => (
                <div key={reason} className="mb-1" data-testid="lb-import-skip-group">
                  <span className="text-slate-200">{list.length} {reason}:</span>{" "}
                  {list
                    .slice()
                    .sort((a, b) => String(a.entry_date || "").localeCompare(String(b.entry_date || "")))
                    .map((s) => `${s.entry_date || "no date"} ${s.hours} h`)
                    .join(", ")}
                </div>
              ))}
            </div>
          )}
          {dups > 0 && (
            <p className="text-amber-400 mb-2" data-testid="lb-import-dups">
              {dups} look{dups === 1 ? "s" : ""} like time already in CaseMinder and {dups === 1 ? "is" : "are"} left out. Tick one to bring it across anyway.
            </p>
          )}
          {preview.stuck.length > 0 && (
            <p className="text-amber-400 mb-2" data-testid="lb-import-stuck">
              {preview.stuck.length} started but not finished: ask for these to be checked before importing again.
            </p>
          )}
          {rows.length > 0 && (
            <table className="w-full mb-2" data-testid="lb-import-rows">
              <tbody>
                {rows.map((c) => {
                  const id = String(c.lawbillity_id);
                  const dup = c.possible_duplicate_of;
                  return (
                  <tr key={id} className={`border-t border-white/5 ${dup && !ticked.has(id) ? "opacity-60" : ""}`}
                    data-testid={dup ? "lb-import-dup-row" : "lb-import-row"}>
                    <td className="py-1 pr-2 w-5">
                      {dup && (
                        <input type="checkbox" checked={ticked.has(id)} onChange={() => toggle(id)}
                          aria-label="Bring across anyway" data-testid={`lb-import-dup-tick-${id}`} />
                      )}
                    </td>
                    <td className="py-1 pr-2 whitespace-nowrap">{c.entry_date}</td>
                    <td className="py-1 pr-2 whitespace-nowrap">{c.fee_earner_email}</td>
                    <td className="py-1 pr-2 whitespace-nowrap" title={`${c.lawbillity_minutes} minutes in LawBillity`}>{c.duration_hours} h</td>
                    <td className="py-1">
                      {c.narrative}
                      {dup && (
                        <span className="block text-amber-400" title={dup.narrative}>
                          <Copy className="inline w-3 h-3 mr-1" />
                          Already in CaseMinder: {dup.duration_hours} h{dup.narrative ? `, "${dup.narrative}"` : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="flex items-center gap-2">
            {rows.length > 0 && (
              <>
                <select
                  value={rateType}
                  onChange={(e) => setRateType(e.target.value)}
                  disabled={!!preview.rate_type || loading}
                  className="bg-transparent border border-white/10 rounded px-2 py-1"
                  data-testid="lb-import-rate"
                  aria-label="Rate"
                >
                  {Object.entries(RATE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <Button size="sm" onClick={runImport} disabled={loading || !preview.matter_open || n === 0} data-testid="lb-import-run-btn">
                  {loading ? "Importing…" : `Import ${n}`}
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={() => setPreview(null)} disabled={loading}>Close</Button>
          </div>
        </div>
      )}
    </div>
  );
}
